import React, { useState } from "react";
import "./hra.css";

const fmtINR = n => '₹' + Number(n).toLocaleString('en-IN');

export default function HraCalculator() {
  const [basic, setBasic]     = useState("");
  const [da, setDa]           = useState("");
  const [hra, setHra]         = useState("");
  const [rent, setRent]       = useState("");
  const [isMetro, setIsMetro] = useState(false);
  const [result, setResult]   = useState(null);

  const calculateHRA = () => {
    const salary      = Number(basic) + Number(da);
    const annualHra   = Number(hra);
    const annualRent  = Number(rent);

    const rule1 = annualHra;
    const rule2 = (isMetro ? 0.5 : 0.4) * salary;
    const rule3 = annualRent - 0.1 * salary;

    const exemptAmount = Math.min(rule1, rule2, rule3 > 0 ? rule3 : Infinity);
    const taxableHra   = annualHra - exemptAmount;
    const exempted     = exemptAmount < 0 ? 0 : exemptAmount;
    const taxable      = taxableHra  < 0 ? 0 : taxableHra;
    const exemptPct    = annualHra > 0 ? Math.round((exempted / annualHra) * 100) : 0;

    setResult({ exempted, taxable, rule1, rule2, rule3: rule3 > 0 ? rule3 : 0, annualHra, exemptPct });
  };

  const rules = result
    ? [
        { label: "Actual HRA received",                     val: result.rule1, note: "Rule 1" },
        { label: `${isMetro ? "50%" : "40%"} of Basic + DA`, val: result.rule2, note: "Rule 2" },
        { label: "Rent paid − 10% of Basic + DA",            val: result.rule3, note: "Rule 3" },
      ]
    : [];

  return (
    <div className="hra-page">

      {/* ── HERO ── */}
      <header className="hra-hero">
        <div className="hra-hero__eyebrow">Income Tax · Section 10(13A)</div>
        <h1 className="hra-hero__title">
          HRA <span className="choose-title-accent">Exemption</span> Calculator
        </h1>
        <p className="hra-hero__sub">
          Find out exactly how much of your House Rent Allowance is tax-exempt — instantly.
        </p>
      </header>

      <div className="hra-layout">

        {/* ── INPUT CARD ── */}
        <div className="hra-card">
          <div className="hra-card__heading">
            <span>📋</span> Enter Your Details
          </div>

          {[
            { label: "Basic Salary",       hint: "Annual ₹", val: basic, set: setBasic },
            { label: "Dearness Allowance", hint: "Annual ₹", val: da,    set: setDa   },
            { label: "HRA Received",       hint: "Annual ₹", val: hra,   set: setHra  },
            { label: "Rent Paid",          hint: "Annual ₹", val: rent,  set: setRent },
          ].map(({ label, hint, val, set }) => (
            <div className="hra-field" key={label}>
              <div className="hra-field__header">
                <span className="hra-field__label">{label}</span>
                <span className="hra-field__hint">{hint}</span>
              </div>
              <div className="hra-field__input-wrap">
                <span className="hra-field__rupee">₹</span>
                <input
                  className="hra-field__input"
                  type="number"
                  placeholder="0"
                  value={val}
                  onChange={e => set(e.target.value)}
                />
              </div>
            </div>
          ))}

          {/* Metro toggle */}
          <div className="hra-metro">
            <div className="hra-metro__text">
              <span className="hra-metro__label">Metro City Resident?</span>
              <span className="hra-metro__desc">Delhi, Mumbai, Chennai, Kolkata → 50% exemption</span>
            </div>
            <button
              className={`hra-toggle${isMetro ? " hra-toggle--on" : ""}`}
              onClick={() => setIsMetro(v => !v)}
              aria-label="Toggle metro city"
            >
              <span className="hra-toggle__thumb" />
            </button>
          </div>

          <div className="hra-metro__badge">
            {isMetro
              ? "🏙️ Metro — 50% of Basic + DA applies"
              : "🏘️ Non-Metro — 40% of Basic + DA applies"}
          </div>

          <button className="hra-btn" onClick={calculateHRA}>
            Calculate HRA Exemption →
          </button>

          <div className="hra-note">
            <strong>How it works:</strong> The exempt amount is the <em>lowest</em> of three rules set by the Income Tax Act — Actual HRA, % of salary, and excess rent over 10% of salary.
          </div>
        </div>

        {/* ── RESULT PANEL ── */}
        <div className="hra-output">

          {!result && (
            <div className="hra-empty">
              <div className="hra-empty__icon">🏠</div>
              <div className="hra-empty__text">
                Fill in your details and click <strong>Calculate</strong> to see your HRA exemption breakdown.
              </div>
            </div>
          )}

          {result && (
            <>
              {/* Main result */}
              <div className="hra-result-card">
                <div className="hra-result-card__bar" />
                <div className="hra-result-card__row">
                  <div className="hra-result-block hra-result-block--accent">
                    <div className="hra-result-block__label">✅ Tax-Exempt HRA</div>
                    <div className="hra-result-block__amount">{fmtINR(result.exempted)}</div>
                    <div className="hra-result-block__sub">{result.exemptPct}% of HRA received</div>
                  </div>
                  <div className="hra-result-block">
                    <div className="hra-result-block__label">⚠️ Taxable HRA</div>
                    <div className="hra-result-block__amount hra-result-block__amount--dark">{fmtINR(result.taxable)}</div>
                    <div className="hra-result-block__sub">{100 - result.exemptPct}% of HRA received</div>
                  </div>
                </div>

                {/* Visual bar */}
                <div className="hra-split-bar">
                  <div className="hra-split-bar__exempt" style={{ width: result.exemptPct + "%" }} />
                  <div className="hra-split-bar__taxable" style={{ width: (100 - result.exemptPct) + "%" }} />
                </div>
                <div className="hra-split-bar__labels">
                  <span className="hra-split-bar__label--exempt">Exempt {result.exemptPct}%</span>
                  <span className="hra-split-bar__label--taxable">Taxable {100 - result.exemptPct}%</span>
                </div>
              </div>

              {/* Three-rule breakdown */}
              <div className="hra-card hra-card--sm">
                <div className="hra-card__heading">
                  <span>📐</span> Three-Rule Breakdown
                </div>
                <p className="hra-rules__desc">
                  The exemption equals the <strong>minimum</strong> of these three values:
                </p>
                <div className="hra-rules">
                  {rules.map((r, i) => {
                    const isMin = r.val === result.exempted && result.exempted > 0;
                    return (
                      <div className={`hra-rule${isMin ? " hra-rule--winner" : ""}`} key={i}>
                        <div className="hra-rule__left">
                          <span className="hra-rule__note">{r.note}</span>
                          <span className="hra-rule__label">{r.label}</span>
                        </div>
                        <div className="hra-rule__right">
                          <span className="hra-rule__val">{fmtINR(r.val)}</span>
                          {isMin && <span className="hra-rule__min-tag">Minimum ✓</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Summary chips */}
              <div className="hra-chips">
                {[
                  { label: "Total HRA Received",      val: fmtINR(result.annualHra) },
                  { label: "Exempt Amount",            val: fmtINR(result.exempted)  },
                  { label: "Added to Taxable Income",  val: fmtINR(result.taxable)   },
                ].map(({ label, val }) => (
                  <div className="hra-chip" key={label}>
                    <span className="hra-chip__label">{label}</span>
                    <span className="hra-chip__val">{val}</span>
                  </div>
                ))}
              </div>

              {/* Tax tip */}
              <div className="hra-tip">
                💡 <strong>Tip:</strong> To maximize exemption, ensure rent receipts are maintained and a rent agreement is in place. For rent &gt; ₹1L/yr, landlord's PAN is mandatory.
              </div>
            </>
          )}
        </div>
      </div>

      <footer className="hra-footer">
        ⚠️ This calculator is for estimation purposes only. Consult a tax advisor for precise figures. Governed by the Income Tax Act, 1961 — Section 10(13A).
      </footer>
    </div>
  );
}